export interface ProductPromoSource {
  promotion_price?: number | null
  sale_price?: number | null
  original_price?: number | null
  price?: number | null
  discount_percent?: number | null
  flash_sales?: Array<{
    status?: string | null
    discount_percent?: number | null
  }> | null
}

/**
 * Resolves the active promotion price for a product/inventory.
 * Evaluates:
 * 1. Inventory variant specific promotion_price
 * 2. Product-level promotion_price
 * 3. Product-level sale_price (when active sale makes sale_price < original_price)
 * 4. Product-level discount_percent
 * 5. Active flash sale discount_percent (status === "active")
 * Returns the discounted price number if an active promotion exists, or null.
 */
export const resolvePromotionPrice = (
  inventoryPrice: number,
  inventoryPromotionPrice: number | null | undefined,
  product?: ProductPromoSource | null
): number | null => {
  const invBase = Number(inventoryPrice)
  if (!Number.isFinite(invBase) || invBase <= 0) {
    return null
  }

  // 1. Inventory-specific promotion_price
  const invPromo = Number(inventoryPromotionPrice)
  if (
    typeof inventoryPromotionPrice === "number" &&
    Number.isFinite(invPromo) &&
    invPromo > 0 &&
    invPromo < invBase
  ) {
    return invPromo
  }

  if (!product) return null

  // 2. Product-level promotion_price
  const prodPromo = Number(product.promotion_price)
  if (Number.isFinite(prodPromo) && prodPromo > 0 && prodPromo < invBase) {
    return prodPromo
  }

  // 3. Product-level sale_price (often set by backend when active promotion exists)
  const prodSale = Number(product.sale_price)
  if (Number.isFinite(prodSale) && prodSale > 0 && prodSale < invBase) {
    return prodSale
  }

  // 4. Product-level discount_percent
  const percent = Number(product.discount_percent)
  if (Number.isFinite(percent) && percent > 0 && percent < 100) {
    return Math.round(invBase * (1 - percent / 100))
  }

  // 5. Active flash sale discount percent
  const activeFlashSale = (product.flash_sales || []).find(
    s => s && s.status === "active"
  )
  const flashPercent = Number(activeFlashSale?.discount_percent)
  if (
    Number.isFinite(flashPercent) &&
    flashPercent > 0 &&
    flashPercent < 100
  ) {
    return Math.round(invBase * (1 - flashPercent / 100))
  }

  return null
}

